import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { getCompilationEndpoint } from "../../client-messages/compilation";
import type { CompilationListItem } from "../components/CompilationList";
import { compilationQuery } from "../queries";
import { isStaleQueued, toViewModel } from "./use-compilations-list";

export interface UseCompilationOptions {
  /** Site the compilation's camera belongs to. */
  siteId?: string | undefined;
  /** `asset@camera` key. Both this and `siteId` are in the endpoint's path. */
  assetCameraKey?: string | undefined;
  requestId?: string | undefined;
  /** Camera key → display name, as in `useCompilationsList`. */
  cameraNames?: ReadonlyMap<string, string> | undefined;
  /** Skip the fetch — e.g. because the list already has this compilation. */
  enabled?: boolean | undefined;
}

export interface CompilationResult {
  readonly item: CompilationListItem | null;
  readonly isLoading: boolean;
}

/**
 * Fetch one compilation by id.
 *
 * Exists for deep links. The list only holds the page currently on screen, so
 * a link to a compilation that has since been pushed off the first page — the
 * normal case for an emailed "your compilation is ready" link read some hours
 * later — finds nothing there. This resolves it directly instead.
 *
 * Disabled unless the site, camera and request id are all present, because the
 * endpoint is camera-scoped: the id alone does not address a compilation.
 *
 * A missing or forbidden compilation resolves to `null` rather than throwing.
 * A stale link is a normal thing for a user to click, not an error worth
 * surfacing through the global query error handler.
 */
export function useCompilation(options: UseCompilationOptions = {}): CompilationResult {
  const apiConfig = useApiConfig();
  const { siteId, assetCameraKey, requestId, cameraNames, enabled = true } = options;
  const { tenant, authenticatedFetch } = apiConfig;

  /**
   * Parsed rather than cast: these arrive from a URL a user can edit, and a
   * malformed pair should read as "no deep link to resolve" rather than
   * throwing on render or sending a nonsense path to the API.
   */
  const pathParams = useMemo(() => {
    if (!siteId || !assetCameraKey || !requestId) return undefined;
    try {
      return {
        siteId: SiteId.fromString(siteId),
        assetCameraKey: AssetCameraKey.fromString(assetCameraKey),
        requestId,
      };
    } catch {
      return undefined;
    }
  }, [siteId, assetCameraKey, requestId]);

  // Keyed through `compilationQuery` when there is something to fetch, so the
  // key matches every other endpoint query and the request mutation's
  // invalidator still reaches it. The placeholder is only ever used while the
  // query is disabled, where the key is never read.
  const queryKey =
    pathParams ? compilationQuery(apiConfig, pathParams, undefined).queryKey : (["compilation", "idle"] as const);

  const query = useQuery({
    queryKey,
    enabled: enabled && !!tenant && !!pathParams,
    queryFn: async ({ signal }) => {
      if (!tenant || !pathParams) throw new Error("Compilation query requires a tenant and a full camera path");

      const url =
        tenant.apiBaseUrl +
        expandEndpoint(getCompilationEndpoint, { path: { tenantId: tenant.tenantId, ...pathParams } });

      const response = await authenticatedFetch(url, { signal });
      if (response.status === 404 || response.status === 403) return null;
      if (!response.ok) throw new Error(`Request failed: ${String(response.status)}`);

      const data: unknown = await response.json();
      return getCompilationEndpoint.responseSchema.parse(data).data;
    },
  });

  const item = useMemo<CompilationListItem | null>(() => {
    if (!query.data) return null;
    return toViewModel(query.data, cameraNames, { stale: isStaleQueued(query.data, Date.now()) });
  }, [query.data, cameraNames]);

  return { item, isLoading: query.isLoading };
}
